import React, { useState, useRef } from 'react';
import { Upload, AlertTriangle, ShieldCheck } from 'lucide-react';
import { DocumentCategory } from '@/types/domain';
import { DOCUMENT_POLICY } from '@/services/api/DocumentCommentService';

interface FileDropzoneProps {
  onUploadFile: (file: File, category: DocumentCategory) => Promise<void>;
  disabled?: boolean;
  maxSizeMb?: number;
}

const CATEGORY_OPTIONS: Array<{ label: string; value: DocumentCategory }> = [
  { label: 'Technical Specification', value: 'TechnicalSpec' },
  { label: 'Scope of Work (SOW)', value: 'ScopeOfWork' },
  { label: 'Commercial Quotation', value: 'Quotation' },
  { label: 'Cost Estimate', value: 'Estimate' },
  { label: 'Technical Drawing', value: 'Drawing' },
  { label: 'Approval Evidence', value: 'ApprovalDoc' },
  { label: 'Other Supporting Document', value: 'Other' },
];

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onUploadFile,
  disabled = false,
  maxSizeMb = DOCUMENT_POLICY.maxFileSizeMb,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory>('TechnicalSpec');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const processFile = async (file: File) => {
    setErrorMessage(null);
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!DOCUMENT_POLICY.allowedExtensions.includes(ext as (typeof DOCUMENT_POLICY.allowedExtensions)[number])) {
      setErrorMessage(
        `File extension ${ext} is not allowed. Whitelisted: ${DOCUMENT_POLICY.allowedExtensions.join(', ')}`
      );
      return;
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
      setErrorMessage(
        `File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds maximum allowed ${maxSizeMb} MB limit.`
      );
      return;
    }

    try {
      setIsUploading(true);
      await onUploadFile(file, selectedCategory);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to upload document. Please retry.';
      setErrorMessage(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  return (
    <div className="space-y-3">
      {/* Category Picker & Controls Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <label className="font-semibold text-foreground">Document Category:</label>
          <select
            className="px-2.5 py-1.5 rounded border border-input bg-background text-xs font-medium focus:outline-none focus:ring-1 focus:ring-primary"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as DocumentCategory)}
            disabled={disabled || isUploading}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-foreground-secondary font-mono">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          <span>Fail-Closed Malware Scanning Enabled</span>
        </div>
      </div>

      {/* Drag & Drop Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
          isDragging
            ? 'border-primary bg-primary/10 scale-[1.01]'
            : 'border-border hover:border-primary/50 hover:bg-surface-hover/30'
        } ${disabled || isUploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={DOCUMENT_POLICY.allowedExtensions.join(',')}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) processFile(file);
          }}
        />

        <div className="flex flex-col items-center gap-2">
          <div className="p-3 rounded-full bg-primary/10 text-primary">
            <Upload className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground">
              {isUploading ? 'Uploading & Scanning Document...' : 'Click to upload or drag & drop file'}
            </p>
            <p className="text-[11px] text-foreground-secondary mt-0.5">
              Whitelisted: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (Max {maxSizeMb} MB)
            </p>
          </div>
        </div>
      </div>

      {/* Error Message Alert */}
      {errorMessage && (
        <div className="p-3 rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-xs flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};
